// rtuWebGatewayService.ts - "Open RTU web app" client: availability, session open,
// keep-alive for sessions opened from this tab, and error classification.
//
// Nothing here ever calls the launch URL (the browser tab navigates to it) and nothing
// logs the launch URL or tokens.

import apiClient from './apiClient';
import authService from './authService';
import keycloak from '../keycloak';
import { API_ENDPOINTS } from '../config/apiConfig';
import { isKeycloakEnabled } from '../config/authConfig';
import { subscribeSessionEpoch } from './sessionIdentity';
import {
  getTrackedRtuWebSessionIds,
  refreshRtuWebOwnerToken,
  subscribeTrackedRtuWebSessions,
  trackRtuWebSession,
  untrackRtuWebSessions,
} from './rtuWebGatewaySessions';

export type RtuWebGatewayAccessMode = 'GUEST' | 'ADMINISTRATOR' | 'NONE';

export interface RtuWebGatewayAvailability {
  enabled: boolean;
  allowed: boolean;
  /** Presentation hint only: the server decides the real mode when the session is opened. */
  mode: RtuWebGatewayAccessMode;
}

export interface RtuWebGatewaySession {
  sessionId: string;
  launchUrl: string;
  expiresAt: string;
}

export interface RtuWebGatewayKeepAliveEntry {
  sessionId: string;
  rtuId?: number;
  state: 'OPEN' | 'CLOSED';
  reason?: string | null;
}

export type RtuWebErrorKind =
  | 'forbidden'
  | 'route'
  | 'unavailable'
  | 'disabled'
  | 'modeChanged'
  | 'adminUnavailable';

export const KEEP_ALIVE_INTERVAL_MS = 60_000;
const AVAILABILITY_TTL_MS = 30_000;
const NOT_AVAILABLE: RtuWebGatewayAvailability = { enabled: false, allowed: false, mode: 'NONE' };

const currentBearerToken = (): string | null => {
  if (isKeycloakEnabled()) {
    return keycloak?.token ?? null;
  }
  return authService.getToken();
};

// ---- availability (lazy, per RTU, short-lived cache, de-duplicated) -----------------

const availabilityCache = new Map<number, { at: number; value: RtuWebGatewayAvailability }>();
const availabilityInFlight = new Map<number, Promise<RtuWebGatewayAvailability>>();

export const resetRtuWebGatewayAvailability = (): void => {
  availabilityCache.clear();
  availabilityInFlight.clear();
};

export const getRtuWebGatewayAvailability = (rtuId: number): Promise<RtuWebGatewayAvailability> => {
  const cached = availabilityCache.get(rtuId);
  if (cached && Date.now() - cached.at < AVAILABILITY_TTL_MS) {
    return Promise.resolve(cached.value);
  }
  const pending = availabilityInFlight.get(rtuId);
  if (pending) {
    return pending;
  }
  const request = apiClient
    .get(API_ENDPOINTS.RTU.WEB_GATEWAY_AVAILABILITY(rtuId))
    .then((response) => {
      const data = response?.data ?? {};
      const allowed = data.allowed === true;
      // Older servers omit `mode`: treat it as GUEST when allowed, NONE otherwise.
      const mode: RtuWebGatewayAccessMode =
        data.mode === 'GUEST' || data.mode === 'ADMINISTRATOR' || data.mode === 'NONE' ? data.mode : allowed ? 'GUEST' : 'NONE';
      const value: RtuWebGatewayAvailability = { enabled: data.enabled === true, allowed, mode };
      availabilityCache.set(rtuId, { at: Date.now(), value });
      return value;
    })
    // 404 / network error / superseded session: simply not enabled (not cached).
    .catch((): RtuWebGatewayAvailability => NOT_AVAILABLE)
    .finally(() => {
      availabilityInFlight.delete(rtuId);
    });
  availabilityInFlight.set(rtuId, request);
  return request;
};

// ---- open / close -------------------------------------------------------------------

/** The only call that logs the user into an RTU. Invoked from a click handler only. */
export const openRtuWebSession = async (rtuId: number, serialNo: string): Promise<RtuWebGatewaySession> => {
  const response = await apiClient.post(API_ENDPOINTS.RTU.WEB_GATEWAY_SESSIONS(rtuId), { serialNo });
  const session = response.data as RtuWebGatewaySession;
  trackRtuWebSession(session.sessionId, currentBearerToken(), rtuId);
  return session;
};

export const closeRtuWebSession = async (sessionId: string): Promise<void> => {
  untrackRtuWebSessions([sessionId]);
  try {
    await apiClient.delete(API_ENDPOINTS.RTU_WEB_GATEWAY.SESSION(sessionId));
  } catch {
    // best effort
  }
};

/**
 * User-initiated close from the RTU menu. Unlike the best-effort close above it reports failure:
 * resolves 'closed' (or 'gone' when the gateway no longer knows the session, 404) after untracking it;
 * any other error is rethrown and the session stays tracked so the user can try again.
 */
export const closeRtuWebSessionExplicitly = async (sessionId: string): Promise<'closed' | 'gone'> => {
  let outcome: 'closed' | 'gone' = 'closed';
  try {
    await apiClient.delete(API_ENDPOINTS.RTU_WEB_GATEWAY.SESSION(sessionId));
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status !== 404) {
      throw error;
    }
    outcome = 'gone';
  }
  untrackRtuWebSessions([sessionId]);
  return outcome;
};

// ---- popup window -------------------------------------------------------------------

export interface RtuWebPopupScreen {
  availWidth: number;
  availHeight: number;
  availLeft?: number;
  availTop?: number;
}

/**
 * Window features for the RTU web app popup: a resizable window that always fits the usable screen
 * (at most 1280x860, never below 640x480) and is centred in it. Browsers may still open a tab.
 */
export const rtuWebPopupFeatures = (screen: RtuWebPopupScreen): string => {
  const availWidth = screen.availWidth > 0 ? screen.availWidth : 0;
  const availHeight = screen.availHeight > 0 ? screen.availHeight : 0;
  const width = Math.max(640, Math.min(1280, availWidth - 40));
  const height = Math.max(480, Math.min(860, availHeight - 60));
  const left = Math.round((screen.availLeft || 0) + Math.max(0, (availWidth - width) / 2));
  const top = Math.round((screen.availTop || 0) + Math.max(0, (availHeight - height) / 2));
  return `popup=yes,resizable=yes,scrollbars=yes,width=${width},height=${height},left=${left},top=${top}`;
};

const ERROR_KIND_BY_CODE: Record<string, RtuWebErrorKind> = {
  RTU_WEB_GATEWAY_DISABLED: 'disabled',
  RTU_WEB_ACCESS_DENIED: 'forbidden',
  RTU_WEB_ROUTE_UNAVAILABLE: 'route',
  RTU_WEB_ROUTE_CHANGED: 'route',
  RTU_WEB_IDENTITY_CHANGED: 'route',
  RTU_WEB_IDENTITY_MISMATCH: 'route',
  RTU_WEB_CREDENTIALS_UNAVAILABLE: 'unavailable',
  RTU_WEB_LOGIN_REJECTED: 'unavailable',
  RTU_WEB_UNREACHABLE: 'unavailable',
  RTU_WEB_OPEN_FAILED: 'unavailable',
  RTU_WEB_MODE_CHANGED: 'modeChanged',
  RTU_WEB_ADMIN_CREDENTIALS_UNAVAILABLE: 'adminUnavailable',
};

export const classifyRtuWebError = (error: unknown): RtuWebErrorKind => {
  const response = (error as { response?: { status?: number; data?: { code?: string; errorCode?: string } } })?.response;
  const code = response?.data?.code ?? response?.data?.errorCode;
  if (code && ERROR_KIND_BY_CODE[code]) {
    return ERROR_KIND_BY_CODE[code];
  }
  if (response?.status === 403) return 'forbidden';
  return 'unavailable';
};

// ---- keep-alive ---------------------------------------------------------------------

let keepAliveTimer: ReturnType<typeof setInterval> | null = null;
let keepAliveInFlight = false;
let visibilityListenerAttached = false;

const stopKeepAlive = () => {
  if (keepAliveTimer !== null) {
    clearInterval(keepAliveTimer);
    keepAliveTimer = null;
  }
  if (visibilityListenerAttached && typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', onVisibilityChange);
    visibilityListenerAttached = false;
  }
};

export const runRtuWebKeepAlive = async (): Promise<void> => {
  const sessionIds = getTrackedRtuWebSessionIds();
  if (sessionIds.length === 0) {
    stopKeepAlive();
    return;
  }
  if (keepAliveInFlight) {
    return;
  }
  keepAliveInFlight = true;
  try {
    refreshRtuWebOwnerToken(currentBearerToken());
    const response = await apiClient.post(API_ENDPOINTS.RTU_WEB_GATEWAY.KEEP_ALIVE, { sessionIds });
    const sessions: RtuWebGatewayKeepAliveEntry[] = Array.isArray(response?.data?.sessions) ? response.data.sessions : [];
    const closed = sessions.filter((entry) => entry?.state === 'CLOSED').map((entry) => entry.sessionId);
    if (closed.length > 0) {
      untrackRtuWebSessions(closed);
    }
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 401) {
      untrackRtuWebSessions(getTrackedRtuWebSessionIds());
    }
    // every other failure is silent; the next tick retries
  } finally {
    keepAliveInFlight = false;
  }
};

function onVisibilityChange() {
  if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
    void runRtuWebKeepAlive();
  }
}

const syncKeepAlive = () => {
  if (getTrackedRtuWebSessionIds().length === 0) {
    stopKeepAlive();
    return;
  }
  if (keepAliveTimer === null) {
    keepAliveTimer = setInterval(() => {
      void runRtuWebKeepAlive();
    }, KEEP_ALIVE_INTERVAL_MS);
  }
  if (!visibilityListenerAttached && typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibilityChange);
    visibilityListenerAttached = true;
  }
};

subscribeTrackedRtuWebSessions(syncKeepAlive);
// ids restored after a reload of this DMS tab: resume renewing them
syncKeepAlive();
subscribeSessionEpoch(resetRtuWebGatewayAvailability);
